// ETHPriceWidget.js - Updated with localStorage caching
import React, { useState, useEffect } from 'react';

function ETHPriceWidget() {
  const [price, setPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        // Check localStorage first
        const storedData = localStorage.getItem('ethPriceData');
        let shouldFetch = true;
        
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          const now = new Date();
          const storedTime = new Date(parsedData.timestamp);
          const hoursSinceUpdate = (now - storedTime) / (1000 * 60 * 60);
          
          // Use cached data if it's less than 24 hours old
          if (hoursSinceUpdate < 24) {
            setPrice(parsedData.price);
            setPriceChange(parsedData.priceChange);
            setLastUpdated(parsedData.timestamp);
            shouldFetch = false;
            setIsLoading(false);
          }
        }
        
        // Fetch new data if needed
        if (shouldFetch) {
          const response = await fetch(
            'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd&include_24hr_change=true'
          );
          
          if (response.ok) {
            const data = await response.json();
            const currentPrice = data.ethereum.usd;
            const currentPriceChange = data.ethereum.usd_24h_change;
            const now = new Date().toISOString();
            
            // Update state
            setPrice(currentPrice);
            setPriceChange(currentPriceChange);
            setLastUpdated(now);
            
            // Save to localStorage
            localStorage.setItem('ethPriceData', JSON.stringify({
              price: currentPrice,
              priceChange: currentPriceChange,
              timestamp: now
            }));
          } else {
            // If API call fails but we have stored data, keep using it
            if (storedData) {
              const parsedData = JSON.parse(storedData);
              setPrice(parsedData.price);
              setPriceChange(parsedData.priceChange);
              setLastUpdated(parsedData.timestamp);
            }
          }
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching ETH price:", err);
        
        // Try to use cached data on error
        const storedData = localStorage.getItem('ethPriceData');
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setPrice(parsedData.price);
          setPriceChange(parsedData.priceChange);
          setLastUpdated(parsedData.timestamp);
        }
        
        setIsLoading(false);
      }
    };
    
    fetchPrice();
    
    // Only try to update once per hour while page is open
    const interval = setInterval(fetchPrice, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      padding: '10px',
      borderRadius: '4px',
      display: 'flex',
      flexDirection: 'column',
      minWidth: '150px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
        <div style={{ fontWeight: 'bold', marginRight: '10px' }}>ETH:</div>
        <div>${price ? price.toFixed(2) : '—'}</div>
        {priceChange !== null && (
          <div style={{
            marginLeft: '5px',
            color: priceChange >= 0 ? '#4CAF50' : '#f44336',
            fontSize: '14px'
          }}>
            {priceChange >= 0 ? '▲' : '▼'} {Math.abs(priceChange).toFixed(1)}%
          </div>
        )}
      </div>
      
      {lastUpdated && (
        <div style={{ fontSize: '10px', color: '#666', textAlign: 'right' }}>
          Updated: {formatLastUpdated(lastUpdated)}
        </div>
      )}
    </div>
  );
}

export default ETHPriceWidget;